import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Paperclip, Upload, X, FileText, Image as ImageIcon, Loader2 } from "lucide-react";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.gif";
const MAX_MB = 20;

const isImage = (f) => (f?.content_type || "").startsWith("image/");
const iconFor = (f) => (isImage(f) ? <ImageIcon className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />);

export default function InvoiceUploader({
  label = "Invoice / Receipts",
  buttonText,
  files,
  onChange,
  required = false,
  multiple = true,
  testId = "invoice-uploader",
  hint = "PDF, JPG, PNG, WEBP · Max 20 MB each",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    if (!multiple && picked.length > 1) {
      toast.error("Only one file allowed");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of picked) {
        if (f.size > MAX_MB * 1024 * 1024) {
          toast.error(`${f.name} exceeds ${MAX_MB} MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
      }
      if (uploaded.length) {
        onChange(multiple ? [...(files || []), ...uploaded] : uploaded.slice(-1));
        toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} uploaded`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (id) => onChange((files || []).filter(f => f.id !== id));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
        <span className="text-[10px] text-slate-500">{hint}</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple={multiple}
        onChange={handlePick}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="w-full border-dashed"
        data-testid={`${testId}-btn`}
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {busy ? "Uploading…" : (buttonText || `Attach ${multiple ? "invoices/receipts" : "invoice"}`)}
      </Button>
      {(files || []).length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded border text-xs" data-testid={`${testId}-chip-${f.id}`}>
              {iconFor(f)}
              <span className="flex-1 truncate">{f.original_filename}</span>
              <span className="text-slate-500 font-mono">{Math.round((f.size || 0) / 1024)} KB</span>
              <button type="button" onClick={() => remove(f.id)} className="text-slate-400 hover:text-red-600" data-testid={`${testId}-remove-${f.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Auth-safe download button for a file_id. Fetches blob with bearer token,
 *  then opens the resulting object URL in a new tab. */
export function InvoiceLinks({ fileIds = [], compact = false }) {
  if (!fileIds?.length) return null;
  const openFile = async (id) => {
    try {
      const r = await api.get(`/files/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.response?.status === 403 ? "You don't have access to this file" : "Unable to open file");
    }
  };
  return (
    <div className={compact ? "flex flex-wrap gap-1.5 mt-1" : "space-y-1 mt-2"}>
      {fileIds.map((id, idx) => (
        <button
          key={id}
          type="button"
          onClick={() => openFile(id)}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-white hover:bg-slate-50 text-xs text-slate-700 ${compact ? "" : "w-fit"}`}
          data-testid={`invoice-link-${id}`}
        >
          <Paperclip className="w-3 h-3" />
          {compact ? `#${idx + 1}` : `Attachment ${idx + 1}`}
        </button>
      ))}
    </div>
  );
}
